import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Button, Breadcrumb, Layout, Modal, PageHeader, Popconfirm, Space, Spin, Table, Typography, message } from 'antd'
import { Link } from "react-router-dom";
import { IconDelete, IconEdit, IconArrowBack, IconMail } from '../../Widgets/Iconos';
import CustomAvatar from "../../Widgets/Avatar/Avatar";
import FloatingButton from "../../Widgets/Floating Button/FloatingButton";
import Logged from "../../../Hooks/Logged";
import axios from 'axios';

import ModalUsuario from './ModalUsuarios';
import ModalUsuariosCliente from './ModalUsuariosCliente';

const { Content } = Layout;
const { Text, Title } = Typography;


const Usuarios = (props) => {
    const user = useContext(Logged);

    const clienteIdFromRoute = props.match?.params?.id ?? null;

    const [loading, setLoading] = useState(false);
    const [visible, setVisible] = useState(false);
    const [modalClienteVisible, setModalClienteVisible] = useState(false);
    const [editingUserId, setEditingUserId] = useState(undefined);

    const [page, setPage] = useState(1);
    const [limit] = useState(20);
    const [total, setTotal] = useState(0);
    const [data, setData] = useState([]);

    const [cliente, setCliente] = useState({});

    const search = props.search || '';

    useEffect(() => {
        if (props.updateFilterSearch) props.updateFilterSearch(true);
    }, [props.updateFilterSearch]);

    const getCliente = useCallback(async () => {
        if (!clienteIdFromRoute) {
            setCliente({});
            return;
        }

        try {
            const response = await axios.get('/clientes/get', {
                params: {
                    id: clienteIdFromRoute,
                },
            });
            setCliente(response?.data?.data || {});
        } catch {
            message.error('Error al traer cliente');
        }
    }, [clienteIdFromRoute]);

    const getUsuarios = useCallback(async (nextPage = 1) => {
        setLoading(true);
        try {
            const response = await axios.get('/usuarios', {
                headers: {
                    Authorization: sessionStorage.getItem('token'),
                },
                params: {
                    page: nextPage,
                    limit,
                    cliente_id: clienteIdFromRoute,
                    search,
                    searching: true,
                },
            });

            const responseData = response?.data?.data || {};
            setPage(nextPage);
            setTotal(responseData.itemCount || 0);
            setData(responseData.itemsList || []);
        } catch {
            message.error('No se pudieron cargar los usuarios.');
        } finally {
            setLoading(false);
        }
    }, [clienteIdFromRoute, limit, search]);

    useEffect(() => {
        getCliente();
    }, [getCliente]);

    useEffect(() => {
        getUsuarios(1);
    }, [getUsuarios]);

    const back = () => {
        props.history.goBack();
    };

    const showModal = (id) => {
        setEditingUserId(id);
        setVisible(true);
    };

    const hideModal = () => {
        setVisible(false);
        setEditingUserId(undefined);
    };

    const showModalCliente = () => {
        setModalClienteVisible(true);
    };

    const hideModalCliente = () => {
        setModalClienteVisible(false);
    };

    const deleteUsuario = async (id) => {
        try {
            if (clienteIdFromRoute) {
                await axios.post('/usuarios/clientes/update', {
                    usuarios: [{ value: id }],
                    cliente_id: null,
                });
                message.success('Usuario actualizado correctamente.');
            } else {
                await axios.delete('/usuarios/delete', {
                    params: { id },
                });
                message.success('Usuario eliminado correctamente.');
            }

            getUsuarios(page);
        } catch {
            if (clienteIdFromRoute) {
                message.error('No se pudo actualizar el usuario.');
            } else {
                message.error('No se pudo eliminar');
            }
        }
    };

    const sendEmail = async (email) => {
        setLoading(true);
        try {
            await axios.put('/password/recovery', { email });
            Modal.success({
                title: 'Restablecer contraseña.',
                content: 'El correo ha sido enviado.',
            });
        } catch {
            Modal.warning({
                title: 'Error',
                content: 'El correo no se ha podido enviar. Verifica que el correo sea correcto.',
            });
        } finally {
            setLoading(false);
        }
    };

    const columns = useMemo(() => ([
        {
            title: 'Usuario',
            key: 'avatar',
            width: 260,
            render: (text, record) => (
                <Space size={12} align="center">
                    <CustomAvatar name={[record.nombre, record.apellido]} image={record.avatar} />
                    <div>
                        <Title level={5} style={{ margin: 0 }}>{record.nombre || 'Sin nombre'}</Title>
                        <Text type="secondary">{record.apellido || 'Sin apellido'}</Text>
                    </div>
                </Space>
            ),
        },
        {
            title: 'Posicion',
            dataIndex: 'posicion',
            key: 'posicion',
            render: (value) => value || '-',
            responsive: ['md'],
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            render: (value) => value || '-',
        },
        {
            title: 'Acciones',
            key: 'actions',
            width: 220,
            render: (text, record) => (
                <Space size="small" wrap>
                    <Popconfirm
                        placement="topRight"
                        title="Deseas enviar un correo de recuperacion de contrasena?"
                        okText="Si"
                        cancelText="No"
                        onConfirm={() => sendEmail(record.email)}
                    >
                        <Button type='primary' title="Recuperar contrasena" icon={<IconMail />} />
                    </Popconfirm>

                    <Button type="primary" onClick={() => showModal(record._id)} title="Editar" icon={<IconEdit />} />

                    {(user?.tipo === 1) ? (
                        <Popconfirm
                            title="Deseas eliminar este usuario?"
                            okText="Si"
                            cancelText="No"
                            onConfirm={() => deleteUsuario(record._id)}
                        >
                            <Button danger title="Eliminar" icon={<IconDelete />} />
                        </Popconfirm>
                    ) : null}
                </Space>
            ),
        },
    ]), [deleteUsuario, sendEmail, user?.tipo]);

    return (
        <>
            <Spin spinning={loading}>
                <PageHeader
                    className="site-page-header custom-page-header"
                    title={clienteIdFromRoute ? (
                        <div className="flex-left" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <Button ghost onClick={back} className="no-border" title="Volver a VMI">
                                <IconArrowBack />
                            </Button>

                            <Breadcrumb separator="de">
                                <Breadcrumb.Item>Usuarios</Breadcrumb.Item>
                                <Breadcrumb.Item>
                                    <Link className="text-gray" title="Ir a clientes" to="/admin/clientes">
                                        {cliente.razon_social || cliente.nombre || 'Cliente'}
                                    </Link>
                                </Breadcrumb.Item>
                            </Breadcrumb>
                        </div>
                    ) : 'Usuarios'}
                    extra={<span className="page-header-extra">{total} Usuarios</span>}
                />

                <Content className="admin-content content-bg pd-1 mb-3">
                    <Table
                        columns={columns}
                        dataSource={data}
                        rowKey="_id"
                        scroll={{ x: 900 }}
                        pagination={{
                            onChange: getUsuarios,
                            current: page,
                            pageSize: limit,
                            total,
                            position: ['bottomLeft'],
                        }}
                        locale={{ emptyText: 'Sin usuarios disponibles' }}
                    />
                </Content>

                {(user && user.tipo !== 3) ? (
                    clienteIdFromRoute ? (
                        <FloatingButton title="Nuevo Usuario" onClick={showModalCliente} />
                    ) : (
                        <FloatingButton title="Nuevo registro" onClick={() => showModal(undefined)} />
                    )
                ) : null}
            </Spin>

            <ModalUsuario
                visible={visible}
                hideModal={hideModal}
                accept={() => {
                    hideModal();
                    getUsuarios(page);
                }}
                update={() => getUsuarios(page)}
                id={editingUserId}
            />

            <ModalUsuariosCliente
                visible={modalClienteVisible}
                hideModal={hideModalCliente}
                accept={() => {
                    hideModalCliente();
                    getUsuarios(page);
                }}
                update={() => getUsuarios(page)}
                id={clienteIdFromRoute}
            />
        </>
    );
};

export default Usuarios;
